'use client'

import { cn } from 'lib/utils'
import translateText from 'app/lib/translatedText'
import { useState, useEffect, useRef } from 'react'
import { IconNavigateDown, IconNavigateUp } from 'components/icons'
import WidgetWrapper from 'components/WidgetWrapper'

interface FAQWidgetProps {
    lang: string
    data: IFAQWidget
    className?: string
    styles?: {
        textColor?: string
        backgroundColor?: string
    }
}

export default function FAQWidget({ lang, data, className, styles }: FAQWidgetProps) {
    const [openIndex, setOpenIndex] = useState<number | null>(null)
    const faqRef = useRef<HTMLDivElement>(null!)

    const toggleQuestion = (index: number) => {
        setOpenIndex(openIndex === index ? null : index)
    }

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (faqRef.current && !faqRef.current.contains(event.target as Node) && openIndex !== null) {
                setOpenIndex(null)
            }
        }

        // Add event listener
        document.addEventListener('mousedown', handleClickOutside)

        // Cleanup
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [openIndex])

    return (
        <>
            <WidgetWrapper
                ref={faqRef}
                className={cn('faq-widget mx-auto w-full my-8 max-w-[1400px]', className)}
                style={{
                    backgroundColor: styles?.backgroundColor,
                    color: styles?.textColor
                }}
            >
                <h2 className='md:text-3xl text-xl font-bold text-center md:mb-8 mb-4'>
                    {data?.title?.tr?.[0]?.text ? translateText(lang, data.title) : 'Frequently Asked Questions'}
                </h2>

                <div className='grid grid-cols-1 gap-2.5 md:gap-4 mx-auto'>
                    {data.questions
                        .map((question, index) => {
                            const questionText = translateText(lang, question.question)
                            const answerText = translateText(lang, question.answer)

                            // Skip if translated text is empty
                            if (!questionText?.trim() || !answerText?.trim()) {
                                return null
                            }

                            return (
                                <div
                                    key={index}
                                    className={cn(
                                        'faq-item border-secondary glassmorphism rounded-lg overflow-hidden transition-all duration-200',
                                        openIndex === index ? 'shadow-md' : 'hover:shadow-sm'
                                    )}
                                >
                                    <button
                                        onClick={() => toggleQuestion(index)}
                                        className='w-full flex justify-between items-center p-4 text-left font-medium focus:outline-none focus:ring-2 focus:ring-primary/50 min-h-[56px]'
                                        aria-expanded={openIndex === index}
                                    >
                                        <span className='block break-words whitespace-normal text-pretty truncate'>
                                            {questionText}
                                        </span>
                                        <span className='ml-2 flex-shrink-0 text-muted-foreground'>
                                            {openIndex === index ? <IconNavigateUp /> : <IconNavigateDown />}
                                        </span>
                                    </button>

                                    <div
                                        className={cn(
                                            'overflow-hidden transition-all duration-300',
                                            openIndex === index ? 'max-h-[1000px]' : 'max-h-0'
                                        )}
                                    >
                                        <div className='p-4 border-t border-muted/40'>
                                            <span className='block break-words whitespace-normal text-pretty'>
                                                {answerText}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )
                        })
                        .filter(Boolean)}
                </div>
            </WidgetWrapper>
        </>
    )
}
